import React, { useState, useMemo } from "react";
import SbuxDialog2 from "../../../../components/SbuxDialog2";
import SbuxTranslate from "../../../../components/SbuxTranslate";
import TemporaryMsgAdd from "../table/temporaryMsg/TemporaryMsgAdd";
import TemporaryMsgEdit from "../table/temporaryMsg/TemporaryMsgEdit";
import TemporaryMsgDisableDialog from "./TemporaryMsgDisableDialog";
import { createOrUpdateTemporaryMsg } from "../../../../services/temporaryMsg";
import { Box, Button, DialogContent, DialogActions } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../../store/types";
import dayjs from "../../../../utils/dayjs";
import { isStartAndEndDateTimeValid } from "../utils";
import { getMediaMgtDataSelector } from "../../../../selectors/mediaMgtSelector";
import {
  MediaItem,
  MediaMgtDataContainer,
} from "../../../../slices/mediaMgtSlice";

// Statuses that cannot be disabled (already disabled, draft without schedule, or expired)
const CANNOT_DISABLE_STATUSES = ["Disabled", "Draft", "Expired"] as const;

interface Language {
  value: string;
  name: string;
  [key: string]: unknown;
}

interface Props {
  title: React.ReactNode;
  open: boolean;
  row: Record<string, unknown>;
  toggleDialogOpen: () => void;
  business?: unknown;
  languages?: Language[];
  sequenceNumber?: number;
  isAddNew: boolean;
  globalConfigUi?: unknown;
}

const TemporaryMsgDialog = ({
  title,
  open,
  row,
  toggleDialogOpen,
  business,
  languages,
  sequenceNumber,
  isAddNew,
  globalConfigUi,
}: Props) => {
  const mediaMgtData = useAppSelector(getMediaMgtDataSelector);
  const dispatch = useAppDispatch();
  const [temporaryMsgRow] = useState<Record<string, unknown>>(row);
  const [buttonDisabled, setButtonDisabled] = useState(true);
  const [isDisable, setIsDisable] = useState(false);
  const [updatedRow, setUpdatedRow] = useState<Record<string, unknown>>(row);
  const [isDateChanged, setIsDateChanged] = useState(false);
  // Monotonically true once the user touches audio — used as a sentinel in
  // shouldConvert. Renamed to clarify it is never reset back to false.
  const [wasAudioEverChanged, setWasAudioEverChanged] = useState(false);

  // Memoize whether the disable button should be enabled
  const canDisable = useMemo(
    () =>
      !CANNOT_DISABLE_STATUSES.includes(
        temporaryMsgRow.status as (typeof CANNOT_DISABLE_STATUSES)[number],
      ),
    [temporaryMsgRow.status],
  );

  // Memoize whether the original row had any audio — temporaryMsgRow is
  // structurally immutable (no setter), so this scan runs once per component
  // lifetime rather than on every handleTemporaryMsgRow callback.
  const originalHasAudio = useMemo(
    () =>
      Object.keys(temporaryMsgRow).some(
        (k) =>
          k.startsWith("message") &&
          !!(temporaryMsgRow[k] as { value?: string })?.value,
      ),
    [temporaryMsgRow],
  );

  const handleDisableOnClick = () => {
    setIsDisable((prevState) => !prevState);
  };

  const getMediaItems = (): MediaItem[] => {
    if (!mediaMgtData) return [];
    if (Array.isArray(mediaMgtData)) return mediaMgtData as MediaItem[];
    return (mediaMgtData as MediaMgtDataContainer).data ?? [];
  };

  const handleSave = async () => {
    let result: Record<string, unknown> | null = null;
    if (isAddNew) {
      result = {
        description: updatedRow.description,
        info: updatedRow.info,
      };
    } else {
      const existingLanguages =
        Object.keys(updatedRow).length > 0 &&
        Object.keys(updatedRow)
          .filter((a) => a.includes("message"))
          .reduce<Record<string, unknown>>((acc, cur) => {
            acc[cur] = updatedRow[cur];
            return acc;
          }, {});

      // Check the original row status to preserve disabled state
      // Use temporaryMsgRow (original) to check if record was originally disabled
      const isOriginallyDisabled = temporaryMsgRow.status === "Disabled";
      const isCurrentlyDisabled = updatedRow.status === "Disabled";

      // True only when the updated row has at least one audio file reference
      // that exists in mediaMgtData — matching the audio-validity standard used
      // in handleTemporaryMsgRow. A row value string without a corresponding
      // mediaMgtData entry is treated as no audio (e.g. the file was removed
      // from the store externally between sessions).
      const mediaItems = getMediaItems();
      const hasAudioInUpdatedRow = (() => {
        let isAnyInvalidFile = false;
        const validFiles: MediaItem[] = [];
        Object.keys(updatedRow).forEach((key) => {
          const msgVal = updatedRow[key] as { value?: string } | undefined;
          if (key.startsWith("message") && msgVal?.value) {
            const found = mediaItems.find(
              (item) => item.filePath === msgVal.value,
            );
            if (!found) isAnyInvalidFile = true;
            else validFiles.push(found);
          }
        });
        return !isAnyInvalidFile && validFiles.length > 0;
      })();

      // Re-enable a disabled record (disabled: false) when the user makes a
      // scheduling-relevant change AND the record still has audio:
      //   1. isDateChanged — user updated schedule options/dates.
      //   2. isAudioChanged — user added or changed audio on a record that already
      //      had valid future dates (e.g. re-attaching audio to a previously-disabled
      //      record without needing to touch the dates again).
      //
      // Keep disabled when:
      //   - Nothing changed / description-only edit.
      //   - Audio was CLEARED (isAudioChanged true but hasAudioInUpdatedRow false).
      const shouldConvert =
        (isDateChanged || wasAudioEverChanged) && hasAudioInUpdatedRow;
      const shouldRemainDisabled =
        (isOriginallyDisabled || isCurrentlyDisabled) && !shouldConvert;

      // Determine draft status based on user's mode selection
      const isDraftMode = updatedRow.mode === "draftMode";

      // For disabled records that stay disabled, preserve original datetime values
      // For draft mode, don't include date/time fields (drafts have no schedule)
      // For active/enabled records, use the updated datetime values
      const dateTimeFields = isDraftMode
        ? {} // Drafts don't have schedule dates/times
        : shouldRemainDisabled
          ? {
              // Preserve original datetime values for disabled records
              startDate: temporaryMsgRow.startDate
                ? dayjs(temporaryMsgRow.startDate as string).format(
                    "YYYY-MM-DD",
                  )
                : undefined,
              startTime: temporaryMsgRow.startTime,
              startTimeZone: temporaryMsgRow.startTimeZone,
              endDate: temporaryMsgRow.endDate
                ? dayjs(temporaryMsgRow.endDate as string).format("YYYY-MM-DD")
                : undefined,
              endTime: temporaryMsgRow.endTime,
              endTimeZone: temporaryMsgRow.endTimeZone,
            }
          : {
              startDate:
                updatedRow.startTimeOption === "immediately"
                  ? dayjs().format("YYYY-MM-DD")
                  : dayjs(updatedRow.startDate as string).format("YYYY-MM-DD"),
              startTime:
                updatedRow.startTimeOption === "immediately"
                  ? dayjs().format("hh:mm A")
                  : updatedRow.startTime,
              startTimeZone:
                updatedRow.startTimeOption === "immediately"
                  ? Intl.DateTimeFormat().resolvedOptions().timeZone
                  : updatedRow.startTimeZone,
              ...(updatedRow.endTimeOption !== "immediately"
                ? {
                    endDate: dayjs(updatedRow.endDate as string).format(
                      "YYYY-MM-DD",
                    ),
                    endTime: updatedRow.endTime,
                    endTimeZone: updatedRow.endTimeZone,
                  }
                : {}),
            };

      result = {
        sequenceNumber: updatedRow.sequenceNumber,
        description: updatedRow.description,
        draft: isDraftMode,
        disabled: shouldRemainDisabled,
        info: updatedRow.info,
        ...dateTimeFields,
        type: updatedRow.type,
        ...(existingLanguages || {}),
        ...(updatedRow.languageMessage as Record<string, unknown>),
      };
    }
    dispatch(createOrUpdateTemporaryMsg(result as Record<string, unknown>));
    toggleDialogOpen();
  };

  const handleTemporaryMsgRow = (
    newUpdatedRow: Record<string, unknown>,
    isAudioChange = false,
    dateChanged = false,
  ) => {
    if (dateChanged) {
      setIsDateChanged(true);
    }
    if (isAudioChange) {
      setWasAudioEverChanged(true);
    }
    if (newUpdatedRow) {
      setUpdatedRow(newUpdatedRow);

      let hasLanguageMessage = false;
      let isAnyInvalidFile = false;
      const validFiles: MediaItem[] = [];
      const mediaItems = getMediaItems();

      Object.keys(newUpdatedRow).forEach((key) => {
        const msgVal = newUpdatedRow[key] as { value?: string } | undefined;
        if (key.startsWith("message") && msgVal?.value) {
          const result = mediaItems.find(
            (item) => item.filePath === msgVal.value,
          );

          if (!result) {
            isAnyInvalidFile = true;
          } else {
            validFiles.push(result);
          }
        }
      });
      hasLanguageMessage = !isAnyInvalidFile && validFiles.length > 0;

      // All 7 params: startDate, endDate, startTime, endTime, startTimeOption,
      // endTimeOption, timeZone. The function signature has no isFirstLoad/8th arg —
      // this is the complete and correct call.
      const isDateTimeValid = isStartAndEndDateTimeValid(
        newUpdatedRow.startDate as string,
        newUpdatedRow.endDate as string,
        newUpdatedRow.startTime as string,
        newUpdatedRow.endTime as string,
        newUpdatedRow.startTimeOption as string,
        newUpdatedRow.endTimeOption as string,
        newUpdatedRow.startTimeZone as string,
      );

      const hasDescription = Boolean(newUpdatedRow.description);
      const hasChangedDate = isDateChanged || dateChanged;
      // originalHasAudio is a component-level useMemo — not re-derived here.
      // Third arm removed: it fired spuriously when mediaMgtData was not yet
      // loaded (getMediaItems returned [], making hasLanguageMessage false for
      // any record with audio). Child components must pass isAudioChange=true
      // whenever audio is touched; that is the sole authoritative signal.
      const hasChangedAudio = wasAudioEverChanged || isAudioChange;
      // Check both original row and current status for disabled state
      const isOriginallyDisabled = temporaryMsgRow.status === "Disabled";
      const isCurrentlyDisabled = newUpdatedRow.status === "Disabled";
      const isDisabledStatus = isOriginallyDisabled || isCurrentlyDisabled;
      // Check if user is in draft mode - drafts have relaxed validation
      const isDraftMode = newUpdatedRow.mode === "draftMode";
      // Audio validation:
      //   - Draft mode: no audio required.
      //   - Disabled record staying disabled (no date change, e.g. clearing audio or
      //     editing description only): audio not required — the record remains disabled.
      //   - Disabled record being converted to Active/Scheduled (date changed) OR any
      //     non-disabled record: audio IS required.
      const audioValidation =
        isDraftMode ||
        hasLanguageMessage ||
        (isDisabledStatus && !hasChangedDate);
      // Date/time validation:
      //   - Draft mode: no schedule, so always valid.
      //   - Date not changed: nothing to validate.
      //   - Date changed (including disabled→active conversion): dates must be valid.
      const dateTimeValidation =
        isDraftMode || !hasChangedDate || isDateTimeValid;
      // For draft mode, only require description change
      // Simplified: (hasChangedAudio || (hasChangedDate && !hasChangedAudio)) → (hasChangedAudio || hasChangedDate)
      // For new records (isAddNew), only description is required
      const shouldEnableButton = isAddNew
        ? hasDescription
        : isDraftMode
          ? hasDescription
          : hasDescription &&
            dateTimeValidation &&
            audioValidation &&
            (hasChangedAudio || hasChangedDate);
      const disableBtn = !shouldEnableButton;

      setButtonDisabled(disableBtn);
    }
  };

  const dialogConfig = {
    title,
    subtitle: !!temporaryMsgRow.status ? (
      <SbuxTranslate>{` - ${temporaryMsgRow.status as string}`}</SbuxTranslate>
    ) : (
      ""
    ),
  };

  return (
    <>
      <SbuxDialog2
        open={open}
        config={dialogConfig}
        handleOnClose={toggleDialogOpen}
      >
        <DialogContent dividers sx={{ overflow: "visible" }}>
          {isAddNew ? (
            <TemporaryMsgAdd
              row={temporaryMsgRow}
              sequenceNumber={sequenceNumber}
              handleTemporaryMsgRow={handleTemporaryMsgRow}
            />
          ) : (
            <TemporaryMsgEdit
              row={temporaryMsgRow}
              handleTemporaryMsgRow={handleTemporaryMsgRow}
            />
          )}
        </DialogContent>

        <DialogActions>
          <Box className="actionContainer">
            {!isAddNew && (
              <Button
                onClick={handleDisableOnClick}
                variant="outlined"
                size="medium"
                disabled={!canDisable}
              >
                <SbuxTranslate>{"Disable"}</SbuxTranslate>
              </Button>
            )}
            <Button
              onClick={handleSave}
              variant="contained"
              size="medium"
              disabled={buttonDisabled}
            >
              <SbuxTranslate>{"Save"}</SbuxTranslate>
            </Button>
            <Button variant="outlined" size="medium" onClick={toggleDialogOpen}>
              <SbuxTranslate>{"Cancel"}</SbuxTranslate>
            </Button>
          </Box>
        </DialogActions>
      </SbuxDialog2>

      {isDisable && (
        <TemporaryMsgDisableDialog
          title={<SbuxTranslate>{"Disable Temporary Message"}</SbuxTranslate>}
          name={row.description as string}
          open={isDisable}
          toggleDialogOpen={toggleDialogOpen}
          toggleCancelDialog={handleDisableOnClick}
          row={updatedRow}
        />
      )}
    </>
  );
};

export default TemporaryMsgDialog;
